import type { Reading } from '@/versions/v1/schema/reading';
import type { KpiTile } from '@/versions/v1/schema/plant-role';

/** Recovery table comparisons stay on the measured reading's own basis. */
export function recoveryTableFields(reading: Reading) {
  const actual = reading.value!;
  const relative = (comparison: number) => {
    const pct = (actual / comparison - 1) * 100;
    return `${pct < 0 ? '−' : '+'}${Math.abs(pct).toFixed(1)}%`;
  };
  return {
    design: reading.design!.toFixed(1),
    target: reading.target!.toFixed(1),
    actual: actual.toFixed(1),
    deltaTarget: relative(reading.target!),
    deltaDesign: relative(reading.design!),
  };
}

/** Pure projection for the legacy role seed: values and their comparisons
 * are formatted together so the seed cannot carry a second observation. */
export function readingKpiFields(reading: Reading, unit = reading.unit): Pick<KpiTile, 'value' | 'sub' | 'tone'> {
  const digits = reading.decimals ?? 0;
  const format = (value: number) => value.toLocaleString('en-US', {
    minimumFractionDigits: digits, maximumFractionDigits: digits,
  });
  const actual = reading.value!;
  const comparison = (value: number) => value.toLocaleString('en-US', {
    minimumFractionDigits: digits,
    maximumFractionDigits: Math.max(digits, String(value).split('.')[1]?.length ?? 0),
  });
  const comparisons: string[] = [];
  if (reading.design !== undefined) comparisons.push(`Design ${comparison(reading.design)} ${unit}`);
  if (reading.target !== undefined) comparisons.push(`${reading.targetBasis === 'budget' ? 'Budget' : 'Target'} ${comparison(reading.target)} ${unit}`);
  if (!comparisons.length && reading.band) {
    comparisons.push(`Band ${reading.band.lo}–${reading.band.hi} ${unit}`);
  }
  const reference = reading.design ?? reading.target;
  const offBand = reading.band && ((reading.band.lo !== undefined && actual < reading.band.lo)
    || (reading.band.hi !== undefined && actual > reading.band.hi));
  const shortfall = reference !== undefined && (reading.higherIsBetter === false ? actual > reference : actual < reference);
  return {
    value: `${format(actual)}${unit === '%' ? '' : ' '}${unit}`,
    sub: comparisons.join(' · '),
    tone: offBand || shortfall ? 'warn' : 'ok',
  };
}
